/**
 * UAPC Anytime - Admin Vendors Page
 * @module AdminVendors
 * @author Osman - Frontend Developer
 * @description Vendor management for administrators
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Vendor management
 */

import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, XCircle, Trash2, MoreVertical } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vendors`);
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorId) => {
    try {
      await axios.put(`${API_URL}/api/admin/vendors/${vendorId}/approve`);
      toast.success('Vendor approved');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to approve vendor');
    }
  };

  const rejectVendor = async (vendorId) => {
    try {
      await axios.put(`${API_URL}/api/admin/vendors/${vendorId}/reject`);
      toast.success('Vendor rejected');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to reject vendor');
    }
  };

  const deleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor? This will also delete all their parts.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(vendor => 
    vendor.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.company_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="p-8" data-testid="admin-vendors">
        <h1 className="font-heading font-black text-3xl text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Vendor Management
        </h1>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 bg-white dark:bg-slate-900"
            data-testid="search-vendors"
          />
        </div>

        {/* Vendors Table */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-100 dark:bg-slate-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Registered</th>
                  <th className="px-4 py-3 text-right text-xs font-heading font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan="6" className="px-4 py-4">
                        <div className="h-6 bg-slate-200 dark:bg-slate-700 animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : filteredVendors.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                      No vendors found
                    </td>
                  </tr>
                ) : (
                  filteredVendors.map((vendor, index) => (
                    <tr key={vendor.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`vendor-row-${index}`}>
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-slate-900 dark:text-white">{vendor.name}</div>
                          <div className="text-sm text-slate-500">{vendor.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-400">
                        {vendor.company_name || '-'}
                      </td>
                      <td className="px-4 py-4 text-slate-600 dark:text-slate-400">
                        {vendor.phone || '-'}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${
                          vendor.is_approved ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                        }`}>
                          {vendor.is_approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-500">
                        {new Date(vendor.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`vendor-actions-${index}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!vendor.is_approved && (
                              <DropdownMenuItem 
                                onClick={() => approveVendor(vendor.id)}
                                className="text-green-600 cursor-pointer"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                            )}
                            {vendor.is_approved && (
                              <DropdownMenuItem 
                                onClick={() => rejectVendor(vendor.id)}
                                className="text-orange-600 cursor-pointer"
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => deleteVendor(vendor.id)}
                              className="text-red-600 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminVendors;
